import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { Button } from "@/components/ui/button";
import { formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { deleteBiometricDevice, fetchBiometricDeviceById, getBiometricDeviceById } from "@/lib/admin/biometricDevicesStore";
import { biometricDeviceFormErrorMessage, formatBiometricDeviceProtocol } from "@/lib/admin/biometricDeviceForm";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { ArrowLeft, Calendar, Cpu, Edit, FileText, Fingerprint, Loader2, Network, Trash2 } from "lucide-react";
import { isAxiosError } from "axios";
import { format, isValid, parseISO } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

function InfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  const display = value.trim() || "—";
  return (
    <div className="flex gap-3 py-3 border-b border-border/40 last:border-0">
      <div className="shrink-0 mt-0.5 text-muted-foreground">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="text-sm text-foreground mt-1 break-words">{display}</p>
      </div>
    </div>
  );
}

export default function BiometricDeviceView() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "biometric_device_can_view");
  const canUpdate = can(user, "biometric_device_can_update");
  const canDelete = can(user, "biometric_device_can_delete");

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    let mounted = true;
    void (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        await fetchBiometricDeviceById(id);
        if (mounted) refresh();
      } catch (e) {
        console.error("[BiometricDeviceView] Load failed", e);
        if (mounted) {
          if (isAxiosError(e) && e.response?.status === 404) setNotFound(true);
          else toast.error(biometricDeviceFormErrorMessage(e));
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id, refresh]);

  const device = useMemo(() => (id ? getBiometricDeviceById(id) : undefined), [id, version]);

  const runDelete = async () => {
    if (!device || isDeleting) return;
    setIsDeleting(true);
    try {
      const r = await deleteBiometricDevice(device.id);
      toast.success(r.message);
      setConfirmDelete(false);
      navigate("/biometric-devices", { replace: true });
    } catch (e) {
      console.error("[BiometricDeviceView] Delete failed", e);
      toast.error(biometricDeviceFormErrorMessage(e));
    } finally {
      setIsDeleting(false);
    }
  };

  if (!canView) {
    return (
      <PermissionDenied
        title="Biometric device access required"
        message="You don’t have permission to view biometric devices. Ask an administrator to grant biometric_device_can_view."
      />
    );
  }

  if (!id) {
    return (
      <Layout title="Biometric device">
        <div className="glass-card p-8 text-sm text-muted-foreground">Missing device id.</div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout title="Biometric device">
        <div className="glass-card p-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin shrink-0" />
          Loading device…
        </div>
      </Layout>
    );
  }

  if (notFound || !device) {
    return (
      <Layout title="Biometric device">
        <div className="space-y-4">
          <div className="glass-card p-8 text-sm text-muted-foreground">Biometric device not found.</div>
          <Button variant="glass" className="gap-2" asChild>
            <Link to="/biometric-devices">
              <ArrowLeft className="w-4 h-4" />
              Back to devices
            </Link>
          </Button>
        </div>
      </Layout>
    );
  }

  const title = device.model.trim() || "Biometric device";

  return (
    <Layout title={title}>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <Link to="/biometric-devices">
              <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div className="min-w-0">
              <h1 className="text-2xl font-display font-bold text-foreground truncate">{title}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {device.driver ? `${device.driver} · ` : ""}
                {formatBiometricDeviceProtocol(device.protocol)}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canUpdate ? (
              <Link to={`/biometric-devices/${device.id}/edit`}>
                <Button variant="glass" className="gap-2">
                  <Edit className="h-4 w-4" />
                  Edit
                </Button>
              </Link>
            ) : null}
            {canDelete ? (
              <Button
                variant="ghost"
                className="gap-2 text-destructive hover:bg-destructive/10"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            ) : null}
          </div>
        </div>

        <div className="glass-card p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/10">
              <Fingerprint className="h-7 w-7 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">{title}</p>
              <p className="text-xs text-muted-foreground mt-0.5 font-mono">ID {device.id}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="glass-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Fingerprint className="w-4 h-4 text-primary shrink-0" />
              <h3 className="font-display font-semibold text-foreground text-sm">Device details</h3>
            </div>
            <InfoRow icon={Fingerprint} label="Model" value={device.model} />
            <InfoRow icon={Cpu} label="Driver" value={device.driver ?? ""} />
            <InfoRow icon={Network} label="Protocol" value={formatBiometricDeviceProtocol(device.protocol)} />
            <InfoRow icon={FileText} label="Wrapper class" value={device.wrapperClass ?? ""} />
            {device.description ? <InfoRow icon={FileText} label="Description" value={device.description} /> : null}
          </div>

          <div className="glass-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-4 h-4 text-primary shrink-0" />
              <h3 className="font-display font-semibold text-foreground text-sm">Record</h3>
            </div>
            <InfoRow
              icon={Calendar}
              label="Created"
              value={isValid(parseISO(device.createdAt)) ? format(parseISO(device.createdAt), "PP") : device.createdAt}
            />
            <InfoRow icon={Calendar} label="Last updated" value={formatCatalogAuditDate(device.updatedAt)} />
          </div>
        </div>

        <ConfirmAlertDialog
          open={confirmDelete}
          onOpenChange={(o) => !o && setConfirmDelete(false)}
          title="Delete this biometric device?"
          description={
            <>
              Are you sure you want to delete <strong className="text-foreground font-semibold">{device.model}</strong>? Devices
              linked to companies must be removed first. This cannot be undone.
            </>
          }
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void runDelete()}
        />
      </div>
    </Layout>
  );
}
